/* IMPORTS */
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import "./CorrelationStyling.css";
import { CorrelationToolMenu } from "./CorrelationToolMenu";
import APIClient from "../../client";
import cropr from "../../../shared/utils/cropr";
import gettext from "../../../shared/utils/gettext";
import {
  BestCorTypes,
  CollectedMeasurementDataTypes,
  CorDetailsTypes,
  DatasetTypes,
  MatrixTypes,
  RawDataTypes,
} from "./ObjectInterfaces";

/* Assigning the prop types */
interface CorrelationToolStartScreenProps {
  clearMeasurementLayers: any;
  datasets: DatasetTypes[];
  did: string;
  toggleMeasurementLayer: any;
}

/* CorrelationToolStartScreen Component */
export const CorrelationToolStartScreen = ({
  clearMeasurementLayers,
  datasets,
  did,
  toggleMeasurementLayer,
}: CorrelationToolStartScreenProps) => {
  const [correlations, setCorrelations] = useState<BestCorTypes[]>();
  const [matrixData, setMatrixData] = useState<MatrixTypes>();
  const [measurementDataCollected, setMeasurementDataCollected] =
    useState<CollectedMeasurementDataTypes>({});
  const [measurementDetails, setMeasurementDetails] = useState<CorDetailsTypes>();
  const [rawData, setRawData] = useState<RawDataTypes>();
  const [startTool, setStartTool] = useState<boolean>(false);

  /* Upon starting the tool, send a POST request to the backend to get correlations with yield */
  useEffect(() => {
    const formData = new FormData();
    formData.append("cropfield_id", did);

    /* API call to Cloudfarm backend */
    APIClient.post(
      "/api/correlations/initial_correlations/",
      { formData: formData },
      cropr.cloudfarm_oauth_token
    )
      .then((response: any) => {
        setCorrelations(response.bestCors);
        setMatrixData(response.matrix);
        setRawData(response.rawData);
        setMeasurementDetails(response.measurementDetails);
      })

      /* Exception Handling */
      .catch((event: any) => {
        console.error(event);
      });
  }, [did]);

  /* Backend Connection sending measurement info and collecting correlation info */
  useEffect(() => {
    if (Object.keys(measurementDataCollected).length === 0) {
      return;
    }
    setCorrelations(undefined);
    setMatrixData(undefined);
    setRawData(undefined);
    setMeasurementDetails(undefined);

    const fetchCorrelations = async () => {
      /* FormData for sending data */
      const formData = new FormData();
      formData.append("cropfield_id", JSON.stringify(measurementDataCollected.cropfieldId));
      formData.append("main_measurement", JSON.stringify(measurementDataCollected.mainMeasurement));
      formData.append(
        "measurements",
        JSON.stringify(measurementDataCollected.selectedMeasurements)
      );
      formData.append(
        "total_correlations",
        JSON.stringify(measurementDataCollected.totalCorrelations)
      );

      /* API call to Cloudfarm backend */
      await APIClient.post(
        "/api/correlations/custom_correlations/",
        { formData: formData },
        cropr.cloudfarm_oauth_token
      )
        .then((response: any) => {
          setCorrelations(response.bestCors);
          setMatrixData(response.matrix);
          setRawData(response.rawData);
          setMeasurementDetails(response.measurementDetails);
        })

        /* Exception Handling */
        .catch((event: any) => {
          console.error(event);
        });
    };
    fetchCorrelations();
  }, [measurementDataCollected]);

  /* JSX */
  return (
    <div>
      <h4>{gettext("The Correlation Tool")}</h4>
      <p>
        {gettext(
          "Start the tool to find potential relationships between the measurements of your cropfield!"
        )}
      </p>
      <div className={"text-center"}>
        <Button
          onClick={() => {
            clearMeasurementLayers();
            setStartTool(!startTool);
          }}
          className="btn btn-primary btn-lg"
        >
          {gettext("Start Tool")}
        </Button>
      </div>

      <div>
        {startTool && (
          <CorrelationToolMenu
            correlations={correlations!}
            clearMeasurementLayers={clearMeasurementLayers}
            datasets={datasets}
            did={did}
            matrixData={matrixData!}
            measurementDetails={measurementDetails!}
            onClick={(measurementData: CollectedMeasurementDataTypes) => {
              setMeasurementDataCollected(measurementData);
            }}
            rawData={rawData!}
            setStartTool={setStartTool}
            startTool={startTool}
            toggleMeasurementLayer={toggleMeasurementLayer}
          />
        )}
      </div>
    </div>
  );
};
